import { RateGroup } from "@/helpers/types/types.ts";
import { getAPIUrl } from "@/stores/ApolloClient/helpers/getAPIUrl.ts";

const clientsToSkipAvailabilitySorting = ["ramarin"];
const clientsSortedByCode = ["campuccino"];

const clientSortConfigs: { clientId: string; sortList: string[] }[] = [
  {
    clientId: "materra",
    sortList: ["Superior room", "Deluxe room", "Deluxe room land view", "Premium room", "Premium suite"],
  },
  {
    clientId: "ramarin",
    sortList: ["Soba", "Bungalov", "Kamp Parking"],
  },
  {
    clientId: "simuni",
    sortList: [
      "Mobilna Kućica Family Suite",
      "Mobilna kućica Family Suite u prvom redu do mora",
      "Mobilna Kućica Pool House",
      "Mobilna kućica Sunset Plus",
      "Mobilna ku\u0107ica Sunset Plus prvi red do mora",
      "Mobilna Kućica Maun",
      "Mobilna Kućica Maun prvi red do mora",
      "Mobilna Kućica Dalmacija",
      "Mobilna kućica Dalmacija prvi red do mora",
      "Mobilna Kućica Šimuni",
      "Mobilna Kućica Holiday",
      "Mobilna ku\u0107ica Holiday prvi red do mora",
      "Mobilna Kućica Premium Sun",
      "Mobilna kućica Premium Sun prvi red do mora",
      "Mobilna Kućica Sunset",
      "Mobilna Kućica Twix",
      "Mobilna Kućica Vista",
      "Mobilna Kućica Vista prvi red do mora",
      "Mobilna Kućica Kornat",
      "Kamping parcela Adriatic Blue",
      "Kamping parcela Pine Green",
      "Kamping parcela Slobodna zona Jug",
      "Kamping parcela Slobodna zona Centar",
    ],
  },
];

export const useClientSpecificSorting = () => {
  const sortRateGroups = (rateGroups: RateGroup[]): RateGroup[] => {
    const apiUrl = getAPIUrl();

    // Sort by rate group code for specific clients
    const shouldSortByCode = clientsSortedByCode.some((client) => apiUrl.includes(client));
    if (shouldSortByCode) {
      return rateGroups.sort((a, b) => (a.code ?? "").localeCompare(b.code ?? ""));
    }

    // Find matching client configuration
    const clientConfig = clientSortConfigs.find((config) => apiUrl.includes(config.clientId));

    if (!clientConfig) {
      // Default sorting by availability if no client-specific config
      return rateGroups.sort((a, b) => {
        if (!a.maxAvailability && !!b.maxAvailability) return 1;
        if (!!a.maxAvailability && !b.maxAvailability) return -1;
        return 0;
      });
    }

    const { sortList } = clientConfig;
    const sortedRateGroups: RateGroup[] = [];

    // Sort according to the predefined list
    sortList.forEach((name) => {
      const rg = rateGroups.find((rg) => rg.name === name);
      if (rg) sortedRateGroups.push(rg);
    });

    // Append any rate groups not present in the sort list. The sort list only
    // orders known accommodations — it must never filter out new or renamed
    // rate groups (e.g. a group renamed in the backend), or they vanish from the UI.
    rateGroups.filter((rg) => !sortList.includes(rg.name)).forEach((rg) => sortedRateGroups.push(rg));

    const shouldSkipAvailabilitySorting = clientsToSkipAvailabilitySorting.some((client) =>
      apiUrl.includes(client),
    );

    // Apply availability sorting for non-ramarin clients
    if (!shouldSkipAvailabilitySorting) {
      sortedRateGroups.sort((a, b) => {
        if (!a.maxAvailability && !!b.maxAvailability) return 1;
        if (!!a.maxAvailability && !b.maxAvailability) return -1;
        return 0;
      });
    }

    return sortedRateGroups;
  };

  return { sortRateGroups };
};
